import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { fetchExamById } from "../Api/examApi";
import SideBar from "../Component/SideBar";

export default function ExamDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [exam, setExam] = useState(null);

  useEffect(() => {
    const getData = async () => {
      try {
        const data = await fetchExamById(id);
        setExam(data);
      } catch (err) {
        console.error("Error fetching exam:", err);
      }
    };
    getData();
  }, [id]);

  if (!exam) return <Typography p={4}>Loading...</Typography>;

  const { generalInformation, markingScheme } = exam;
  const {
    name,
    subject,
    topic,
    language,
    testDate,
    durationInMinutes,
    instructions,
    totalQuestions,
  } = generalInformation;

  const { marksPerQuestion, marksCorrectAnswer, marksWrongAnswer } =
    markingScheme;

  const totalMarks = (marksPerQuestion || 0) * (totalQuestions || 0);
  const formatDate = (dateStr) =>
    dateStr ? new Date(dateStr).toLocaleDateString() : "-";

  return (
    <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}>
      <SideBar />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 3, md: 4 },
          mt: { xs: 2, sm: "50px" },
          minHeight: "100vh",
          bgcolor: "#f4f6f8",
        }}
      >
        <Paper
          elevation={4}
          sx={{
            width: "100%",
            maxWidth: "1100px",
            mx: "auto",
            p: { xs: 4, sm: 2 },
            borderRadius: 3,
            background: "linear-gradient(135deg, #ffffff, #f0f7ff)",
          }}
        >
          {/* Exam Title */}
          <Typography
            variant={isMobile ? "h5" : "h3"}
            fontWeight="bold"
            align="center"
            sx={{
              fontFamily: '"Times New Roman", Times, serif',
              color: "#2c3e50",
              wordBreak: "break-word",
            }}
          >
            {name}
          </Typography>

          <Divider sx={{ mb: 1 }} />

          {/* General Info */}
          <Grid container spacing={2} sx={{ p: { xs: 1, sm: 2 } }}>
            {[
              { label: "Subject", value: subject },
              { label: "Language", value: language },
              { label: "Topic", value: topic },
              { label: "Duration", value: `${durationInMinutes} Min` },
              { label: "Test Date", value: formatDate(testDate) },
            ].map((item, index) => (
              <Grid key={index} item xs={12} sm={6} md={3}>
                <Typography variant="body1">
                  <b>{item.label}:</b> {item.value}
                </Typography>
              </Grid>
            ))}
          </Grid>

          {/* Marking Scheme */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1, sm: 2 },
              borderRadius: 2,
              backgroundColor: "#fafafa",
            }}
          >
            <Typography
              variant={isMobile ? "h6" : "h5"}
              fontWeight="bold"
              gutterBottom
              sx={{ fontFamily: '"Times New Roman", Times, serif' }}
            >
              Marking Scheme
            </Typography>
            <Divider sx={{ mb: 1 }} />

            <Grid container spacing={1}>
              {[
                { label: "Total Questions", value: totalQuestions },
                { label: "Total Marks", value: totalMarks },
                { label: "Marks per Question", value: marksPerQuestion },
                { label: "Correct Answer", value: marksCorrectAnswer },
                { label: "Wrong Answer", value: marksWrongAnswer },
              ].map((item, index) => (
                <Grid key={index} item xs={12} sm={6} md={3}>
                  <Typography variant="body1">
                    <b>{item.label}:</b> {item.value}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Paper>

          {/* Instructions */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1, sm: 2 },
              borderRadius: 3,
              mb: 1,
              maxHeight: { xs: "250px", md: "300px" },
              overflowY: "auto",
              wordBreak: "break-word",
            }}
          >
            <Typography
              variant={isMobile ? "h6" : "h5"}
              fontWeight="bold"
              gutterBottom
              sx={{ fontFamily: '"Times New Roman", Times, serif' }}
            >
              Instructions
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Typography
              variant="body1"
              sx={{
                fontFamily: '"Times New Roman", Times, serif',
                whiteSpace: "pre-line",
              }}
            >
              {instructions}
            </Typography>
          </Paper>

          {/* Start Button */}
          <Box textAlign="center">
            <Button
              variant="contained"
              size={isMobile ? "small" : "medium"} // smaller on mobile
              fullWidth={isMobile}
              sx={{
                background: "linear-gradient(90deg, #43a047, #66bb6a)",
                fontWeight: 600,
                fontSize: isMobile ? "0.85rem" : "1rem", // smaller text on mobile
                py: isMobile ? 1 : 2, // less vertical padding on mobile
                px: isMobile ? 2 : 4, // less horizontal padding on mobile
                mt: 1,
                borderRadius: 3,
                maxWidth: { xs: "100%", sm: "250px" },
                minHeight: isMobile ? "36px" : "48px", // shorter height on mobile
                "&:hover": {
                  background: "linear-gradient(90deg, #388e3c, #43a047)",
                },
              }}
              onClick={() => navigate(`/exam/${id}/questions`)}
            >
              START TEST
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
